import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Popover as P } from 'radix-ui';
import { CaretUpDownIcon, CheckIcon, MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/cn';
import { floatingPanel } from './overlay-styles';

export interface ComboOption {
  value: string;
  label: string;
  hint?: ReactNode;
}

interface BaseProps {
  options: ComboOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  id?: string;
  /** Accessible name of the trigger when there is no visible <label for>. */
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

/** Accent/case-insensitive match so "μαρια" finds "Μαρία". */
export function normalizeSearch(s: string) {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('el-GR');
}

function useFilter(options: ComboOption[], q: string) {
  return useMemo(() => {
    const n = normalizeSearch(q.trim());
    return n ? options.filter((o) => normalizeSearch(o.label).includes(n)) : options;
  }, [options, q]);
}

function OptionList({
  items,
  isSelected,
  onPick,
  emptyText,
  query,
  setQuery,
  searchPlaceholder,
  listId,
}: {
  items: ComboOption[];
  isSelected: (v: string) => boolean;
  onPick: (v: string) => void;
  emptyText: string;
  query: string;
  setQuery: (q: string) => void;
  searchPlaceholder: string;
  listId: string;
}) {
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const it = items[active];
      if (it) onPick(it.value);
    }
  };
  return (
    <div className="flex max-h-80 flex-col">
      <div className="flex items-center gap-2 border-b border-hairline px-3">
        <MagnifyingGlassIcon className="size-4 text-ink-subtle" />
        <input
          autoFocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKey}
          placeholder={searchPlaceholder}
          role="combobox"
          aria-expanded
          aria-controls={listId}
          aria-activedescendant={items[active] ? `${listId}-${active}` : undefined}
          className="h-10 w-full bg-transparent text-[0.875rem] text-ink outline-none placeholder:text-ink-subtle"
        />
      </div>
      <div ref={listRef} id={listId} role="listbox" className="overflow-y-auto p-1">
        {items.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-ink-muted">{emptyText}</p>
        ) : (
          items.map((o, i) => {
            const sel = isSelected(o.value);
            return (
              <div
                key={o.value}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={sel}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onPick(o.value)}
                className={cn(
                  'flex cursor-default items-center gap-2.5 rounded-sm px-2.5 py-2 text-[0.875rem] text-ink-soft',
                  i === active && 'bg-sunken text-ink',
                )}
              >
                <span
                  className={cn(
                    'inline-flex size-4 items-center justify-center rounded-[4px] border',
                    sel ? 'border-primary bg-primary text-on-primary' : 'border-control-border',
                  )}
                >
                  {sel && <CheckIcon weight="bold" className="size-3" />}
                </span>
                <span className="truncate">{o.label}</span>
                {o.hint && <span className="ml-auto text-xs text-ink-subtle">{o.hint}</span>}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const trigger =
  'flex min-h-9.5 w-full items-center gap-2 rounded-sm border border-control-border bg-surface px-3 text-left text-[0.875rem] text-ink ' +
  'shadow-[0_1px_0_var(--gh-hairline)] transition-[border-color,box-shadow] hover:border-ink-subtle/60 ' +
  'focus:outline-none focus-visible:border-primary focus-visible:shadow-[0_0_0_3px_var(--gh-primary-soft)] ' +
  'disabled:cursor-not-allowed disabled:bg-sunken aria-[invalid=true]:border-negative';

export function Combobox({
  value,
  onChange,
  placeholder = '–',
  searchPlaceholder = 'Αναζήτηση…',
  emptyText = 'Δεν βρέθηκε τίποτα',
  options,
  className,
  disabled,
  invalid,
  id,
  ...aria
}: BaseProps & { value: string | null; onChange: (v: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const items = useFilter(options, q);
  const listId = useId();
  const selected = options.find((o) => o.value === value);
  return (
    <P.Root open={open} onOpenChange={(o) => (setOpen(o), o || setQ(''))}>
      <P.Trigger
        id={id}
        disabled={disabled}
        aria-invalid={invalid || aria['aria-invalid'] || undefined}
        aria-label={aria['aria-label']}
        aria-describedby={aria['aria-describedby']}
        className={cn(trigger, className)}
      >
        <span className={cn('flex-1 truncate', !selected && 'text-ink-subtle')}>
          {selected?.label ?? placeholder}
        </span>
        <CaretUpDownIcon className="size-4 text-ink-subtle" />
      </P.Trigger>
      <P.Portal>
        <P.Content
          align="start"
          sideOffset={6}
          className={cn(floatingPanel, 'w-[var(--radix-popover-trigger-width)] min-w-56')}
        >
          <OptionList
            items={items}
            listId={listId}
            query={q}
            setQuery={setQ}
            searchPlaceholder={searchPlaceholder}
            emptyText={emptyText}
            isSelected={(v) => v === value}
            onPick={(v) => {
              onChange(v === value ? null : v);
              setOpen(false);
              setQ('');
            }}
          />
        </P.Content>
      </P.Portal>
    </P.Root>
  );
}

export function MultiSelect({
  value,
  onChange,
  placeholder = '–',
  searchPlaceholder = 'Αναζήτηση…',
  emptyText = 'Δεν βρέθηκε τίποτα',
  options,
  className,
  disabled,
  invalid,
  id,
  max,
  ...aria
}: BaseProps & { value: string[]; onChange: (v: string[]) => void; max?: number }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const items = useFilter(options, q);
  const listId = useId();
  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);
  const toggle = (v: string) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else if (!max || value.length < max) onChange([...value, v]);
  };
  return (
    <P.Root open={open} onOpenChange={(o) => (setOpen(o), o || setQ(''))}>
      <P.Anchor asChild>
        <div
          className={cn(
            trigger,
            'flex-wrap py-1',
            disabled && 'pointer-events-none bg-sunken opacity-70',
            className,
          )}
          aria-invalid={invalid || aria['aria-invalid'] || undefined}
        >
          {value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-[4px] border border-hairline bg-canvas py-0.5 pl-2 pr-1 text-sm text-ink"
            >
              {byValue.get(v)?.label ?? v}
              <button
                type="button"
                className="rounded-[3px] p-0.5 text-ink-subtle hover:bg-sunken hover:text-ink"
                aria-label={`Αφαίρεση ${byValue.get(v)?.label ?? v}`}
                onClick={() => toggle(v)}
              >
                <XIcon className="size-3" />
              </button>
            </span>
          ))}
          <P.Trigger
            id={id}
            aria-label={aria['aria-label']}
            aria-describedby={aria['aria-describedby']}
            className={cn(
              'flex flex-1 items-center justify-between gap-2 py-1 text-left outline-none',
              value.length ? 'min-w-16' : 'min-w-0',
            )}
          >
            <span className="truncate text-ink-subtle">{value.length ? '' : placeholder}</span>
            <CaretUpDownIcon className="size-4 text-ink-subtle" />
          </P.Trigger>
        </div>
      </P.Anchor>
      <P.Portal>
        <P.Content
          align="start"
          sideOffset={6}
          className={cn(floatingPanel, 'w-[var(--radix-popper-anchor-width)] min-w-56')}
        >
          <OptionList
            items={items}
            listId={listId}
            query={q}
            setQuery={setQ}
            searchPlaceholder={searchPlaceholder}
            emptyText={emptyText}
            isSelected={(v) => value.includes(v)}
            onPick={toggle}
          />
        </P.Content>
      </P.Portal>
    </P.Root>
  );
}
